import { Actions, Getters, Module, Mutations, createMapper } from 'vuex-smart-module';
import { AlbumUploadService } from '@/api';
import { APIError } from '@/api/api';
import { AlbumUpload } from '@/types/albumUpload';

// Пока идёт обработка — частим, чтобы прогресс не отставал от страницы загрузки.
// Когда активной обработки нет — редкий тик, только чтобы заметить новые загрузки
// (контракт просит не долбить список для обзора).
const FAST_INTERVAL = 2000;
const SLOW_INTERVAL = 10000;

class AlbumUploadState {
  // Активные загрузки из GET /album-upload (draft/processing/error).
  uploads: AlbumUpload[] = [];
  // Только что завершившиеся (done) — держим локально до закрытия крестиком,
  // т.к. из списка done пропадает.
  completed: AlbumUpload[] = [];
  // id, которые мы уже видели активными — чтобы поймать момент исчезновения.
  knownIds: number[] = [];
  polling = false;
  ticking = false;
  pollTimer: number | null = null;
}

class AlbumUploadGetters extends Getters<AlbumUploadState> {
  // Что показывать в доке: активные + завершённые.
  get visibleUploads(): AlbumUpload[] {
    return [...this.state.uploads, ...this.state.completed];
  }

  get hasVisible(): boolean {
    return this.state.uploads.length > 0 || this.state.completed.length > 0;
  }
}

class AlbumUploadMutations extends Mutations<AlbumUploadState> {
  setUploads(uploads: AlbumUpload[]) {
    this.state.uploads = uploads;
  }

  setKnownIds(ids: number[]) {
    this.state.knownIds = ids;
  }

  addCompleted(upload: AlbumUpload) {
    if (!this.state.completed.some(u => u.id === upload.id)) {
      this.state.completed = [...this.state.completed, upload];
    }
  }

  removeCompleted(id: number) {
    this.state.completed = this.state.completed.filter(u => u.id !== id);
  }

  removeUpload(id: number) {
    this.state.uploads = this.state.uploads.filter(u => u.id !== id);
    this.state.knownIds = this.state.knownIds.filter(knownId => knownId !== id);
  }

  setPolling(value: boolean) {
    this.state.polling = value;
  }

  setTicking(value: boolean) {
    this.state.ticking = value;
  }

  setPollTimer(timer: number | null) {
    this.state.pollTimer = timer;
  }
}

class AlbumUploadActions extends Actions<
  AlbumUploadState,
  AlbumUploadGetters,
  AlbumUploadMutations,
  AlbumUploadActions
> {
  async fetchList() {
    let list: AlbumUpload[];

    try {
      list = await AlbumUploadService.getList();
    } catch (e) {
      // Сетевой сбой/разлогин — не валим док, просто пропускаем тик.
      return;
    }

    // Черновики не показываем: за ними нет фоновой работы (это текущая форма
    // либо брошенный черновик, который сервер подчистит сам). Док — только про
    // то, что реально в обработке или с ошибкой.
    const active = list.filter(u => u.status !== 'draft');
    const activeIds = active.map(u => u.id);

    // id, которые были активны, но ушли из списка — значит done или удалены.
    const disappeared = this.state.knownIds.filter(id => !activeIds.includes(id));

    // Обновляем активные сразу, до await в цикле ниже: иначе исчезнувшая
    // загрузка какое-то время висела бы и в uploads (старая), и в completed —
    // дубль :key в доке.
    this.mutations.setUploads(active);

    const stillUnknown: number[] = [];
    for (const id of disappeared) {
      try {
        const upload = await AlbumUploadService.getOne(id);
        if (upload.status === 'done') {
          this.mutations.addCompleted(upload);
        }
      } catch (e) {
        // 404 — загрузка удалена, забываем. Иная ошибка (сеть) — возвращаем id
        // в knownIds, чтобы повторить попытку в следующем тике и не потерять
        // переход в «Готово».
        if ((e as APIError)?.statusCode !== 404) {
          stillUnknown.push(id);
        }
      }
    }

    this.mutations.setKnownIds([...activeIds, ...stillUnknown]);
  }

  startPolling() {
    if (this.state.polling) {
      return;
    }
    this.mutations.setPolling(true);
    this.actions.tick();
  }

  async tick() {
    if (!this.state.polling) {
      return;
    }
    // Уже идёт опрос (например refreshNow попал во время висящего запроса) —
    // не запускаем второй цикл, текущий сам перепланирует следующий тик.
    if (this.state.ticking) {
      return;
    }
    this.mutations.setTicking(true);

    try {
      await this.actions.fetchList();
    } finally {
      this.mutations.setTicking(false);
    }

    if (!this.state.polling) {
      return;
    }

    const hasActive = this.state.uploads.some(u => u.status === 'processing');
    const delay = hasActive ? FAST_INTERVAL : SLOW_INTERVAL;
    const timer = window.setTimeout(() => this.actions.tick(), delay);
    this.mutations.setPollTimer(timer);
  }

  stopPolling() {
    this.mutations.setPolling(false);
    if (this.state.pollTimer !== null) {
      window.clearTimeout(this.state.pollTimer);
      this.mutations.setPollTimer(null);
    }
  }

  // Немедленно опросить список и перепланировать цикл — например сразу после
  // commit, чтобы новая загрузка появилась в доке без ожидания редкого тика.
  refreshNow() {
    if (!this.state.polling) {
      return;
    }
    if (this.state.pollTimer !== null) {
      window.clearTimeout(this.state.pollTimer);
      this.mutations.setPollTimer(null);
    }
    this.actions.tick();
  }

  dismiss(id: number) {
    this.mutations.removeCompleted(id);
  }

  async retry(id: number) {
    await AlbumUploadService.retry(id);
    await this.actions.fetchList();
  }

  async remove(id: number) {
    await AlbumUploadService.remove(id);
    this.mutations.removeUpload(id);
    this.mutations.removeCompleted(id);
  }
}

export const albumUpload = new Module({
  state: AlbumUploadState,
  getters: AlbumUploadGetters,
  mutations: AlbumUploadMutations,
  actions: AlbumUploadActions
});

export const albumUploadMapper = createMapper(albumUpload);
